'use client';

import './DatePicker.scss';

import { cn } from '@core/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Calendar } from 'react-calendar';

import SvgCalendarBlank from '../../../__generated__/icon/SvgCalendarBlank';
import SvgChevronleft from '../../../__generated__/icon/SvgChevronleft';
import SvgChevronright from '../../../__generated__/icon/SvgChevronright';
import SvgChevronrightDouble from '../../../__generated__/icon/SvgChevronrightDouble';
import SvgChevronrleftDouble from '../../../__generated__/icon/SvgChevronrleftDouble';
import SvgModalclose from '../../../__generated__/icon/SvgModalclose';

type CalendarValuePiece = Date | null;
type CalendarValue =
  CalendarValuePiece | [CalendarValuePiece, CalendarValuePiece];

import { Input } from '../Input/Input';

interface DatePickerProps {
  /** YYYY-MM-DD */
  value: string | null;
  onChange: (date: string) => void;
  placeholder?: string;
  className?: string;
  /** YYYY-MM-DD */
  minDate?: string;
  /** YYYY-MM-DD */
  maxDate?: string;
  disabled?: boolean;
  dimensions?: 'H40';
  contentClassName?: string;
  onClear?: () => void;
  /** 외부에서 open 상태 제어 */
  open?: boolean;
  /** 외부에서 open 상태 변경 핸들러 */
  onOpenChange?: (open: boolean) => void;
}

export const DatePicker = ({
  value,
  onChange,
  placeholder,
  className,
  minDate,
  maxDate,
  disabled,
  dimensions,
  contentClassName,
  onClear,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: DatePickerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen ?? internalOpen;
  const setOpen = (newOpen: boolean) => {
    setInternalOpen(newOpen);
    externalOnOpenChange?.(newOpen);
  };

  const outerValue = value ? dayjs(value).format('YYYY-MM-DD') : '';
  const [inputValue, setInputValue] = useState(outerValue);

  useEffect(() => {
    setInputValue(value ? dayjs(value).format('YYYY-MM-DD') : '');
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    //! 값을 지우는건 onClear 버튼으로만 가능하도록. 안그러면 외부,내부 state 불일치 발생 가능성 생김
    if (!inputValue) {
      setInputValue(outerValue);

      return;
    }

    if (!dayjs(inputValue).isValid()) {
      setInputValue(outerValue);

      return;
    }

    onChange(dayjs(inputValue).format('YYYY-MM-DD'));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative flex items-center', className)}>
        <Input
          type="text"
          dimensions={dimensions}
          className={cn('w-200 min-w-fit', className)}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          aria-label="날짜 입력"
          rightContent={
            <div className="flex items-center gap-4">
              {value && onClear ? (
                <button
                  aria-label="날짜 초기화"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear?.();
                  }}
                >
                  <SvgModalclose
                    className="size-12 text-Gray-700"
                    originalFill
                  />
                </button>
              ) : null}
              <PopoverTrigger asChild disabled={disabled}>
                <button type="button" className="ml-8" aria-label="달력 열기">
                  <SvgCalendarBlank className="size-12 text-Gray-700" />
                </button>
              </PopoverTrigger>
            </div>
          }
        />
      </div>
      <PopoverContent className={cn('z-[1] w-fit', contentClassName)}>
        <div className="mt-8 rounded-small border border-Gray-200 bg-Gray-white p-16 shadow-lg">
          <Calendar
            className="common-date-picker"
            maxDate={maxDate ? new Date(maxDate) : undefined}
            minDate={minDate ? new Date(minDate) : undefined}
            navigationAriaLive="polite"
            next2Label={<SvgChevronrightDouble />}
            nextLabel={<SvgChevronright />}
            prev2Label={<SvgChevronrleftDouble />}
            prevLabel={<SvgChevronleft />}
            value={value?.toString()}
            onChange={(nextValue: CalendarValue) => {
              const selectedDate = Array.isArray(nextValue)
                ? nextValue[0]
                : nextValue;

              if (dayjs(selectedDate).isValid()) {
                onChange(dayjs(selectedDate).format('YYYY-MM-DD'));
                setOpen(false);
              }
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
